/**
 * BDM "My Team" page — full profile view of every BDA/BDE reporting to the BDM.
 * Accessible from the sidebar under the Workspace group (bdmOnly).
 */
import { useEffect, useMemo, useState } from 'react';
import {
    RefreshCw, Search, User as UserIcon, Mail, Phone, MapPin,
    Briefcase, Building2, Star, Calendar, CheckCircle2, ChevronDown, ChevronUp,
} from 'lucide-react';
import api from '../../utils/api';

const USER_STATUS_CHIP = {
    active:    'bg-emerald-100 text-emerald-800',
    pending:   'bg-amber-100 text-amber-800',
    suspended: 'bg-rose-100 text-rose-800',
    rejected:  'bg-rose-100 text-rose-800',
};

const DESIG_LABEL = {
    bda: 'BDA', bde: 'BDE', bdm: 'BDM', bhead: 'BHead',
    operations: 'Operations', performance_marketing: 'Perf. Marketing', other: 'Other',
};

const fd = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function MyTeam() {
    const [data, setData]         = useState(null);
    const [loading, setLoading]   = useState(false);
    const [q, setQ]               = useState('');
    const [desigFilter, setDesigFilter] = useState('all'); // all | bda | bde
    const [expanded, setExpanded] = useState(null);

    const load = () => {
        setLoading(true);
        api.get('/users/my-team')
            .then(r => setData(r.data))
            .catch(() => setData({ ok: false, team: [] }))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const members = useMemo(() => {
        let all = data?.team || [];
        if (desigFilter !== 'all') {
            all = all.filter(u => u.salesDesignation === desigFilter);
        }
        if (!q.trim()) return all;
        const rx = new RegExp(q.trim(), 'i');
        return all.filter(u =>
            rx.test(u.name) || rx.test(u.email) || rx.test(u.phone || '') ||
            rx.test(u.designation || '') || rx.test(u.officeBranch || '') ||
            rx.test(DESIG_LABEL[u.salesDesignation] || '')
        );
    }, [data, q, desigFilter]);

    return (
        <div className="space-y-5">
            {/* Page header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">My Team</h1>
                    <p className="text-sm text-ink-500">
                        All BDA / BDE members reporting to you — full profile details.
                    </p>
                </div>
                <button onClick={load} className="ag-btn-ghost" disabled={loading}>
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
            </div>

            {/* Stats strip */}
            {data?.team && (
                <div className="grid grid-cols-3 gap-3">
                    <StatCard label="Total members" value={data.team.length} />
                    <StatCard label="Active"
                        value={data.team.filter(u => u.status === 'active').length}
                        color="text-emerald-600" />
                    <StatCard label="Pending / Other"
                        value={data.team.filter(u => u.status !== 'active').length}
                        color="text-amber-600" />
                </div>
            )}

            {/* Search + role filter */}
            <div className="ag-card p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Search className="h-4 w-4 text-ink-500 shrink-0" />
                    <input
                        className="ag-input flex-1"
                        placeholder="Search name, email, phone, designation, office…"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                    />
                </div>
                <label className="flex items-center gap-2 shrink-0 sm:ml-auto">
                    <span className="text-xs font-semibold text-ink-500">Filter:</span>
                    <select
                        className="ag-input w-full sm:w-auto sm:min-w-[140px] text-sm"
                        value={desigFilter}
                        onChange={e => setDesigFilter(e.target.value)}
                        aria-label="Filter by role"
                    >
                        <option value="all">All roles</option>
                        <option value="bda">BDA</option>
                        <option value="bde">BDE</option>
                    </select>
                </label>
                <span className="text-xs text-ink-500 shrink-0">{members.length} of {data?.team?.length || 0}</span>
            </div>

            {/* States */}
            {loading && (
                <div className="ag-card p-10 text-center text-sm text-ink-500">Loading team…</div>
            )}
            {!loading && data?.ok === false && (
                <div className="ag-card p-8 text-center text-sm text-rose-600">
                    Failed to load team members. You may not have BDM designation assigned.
                </div>
            )}
            {!loading && data?.ok && members.length === 0 && (
                <div className="ag-card p-8 text-center text-sm text-ink-500">
                    {q || desigFilter !== 'all' ? 'No members match your filters.' : 'No team members found. BDA / BDE users from your office branch will appear here once they are active.'}
                </div>
            )}

            {/* Member cards */}
            {!loading && members.length > 0 && (
                <div className="space-y-3">
                    {members.map(u => (
                        <MemberCard
                            key={u._id} user={u}
                            expanded={expanded === u._id}
                            onToggle={() => setExpanded(expanded === u._id ? null : u._id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Stat strip card ─────────────────────────────────────────── */
function StatCard({ label, value, color = 'text-ink-900' }) {
    return (
        <div className="ag-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">{label}</div>
            <div className={`text-2xl font-extrabold mt-1 tabular-nums ${color}`}>{value}</div>
        </div>
    );
}

/* ─── Member profile card ─────────────────────────────────────── */
function MemberCard({ user: u, expanded, onToggle }) {
    const initials = (u.name || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

    return (
        <div className="ag-card overflow-hidden">
            {/* Summary row — always visible */}
            <div
                className="p-4 flex items-center gap-4 cursor-pointer hover:bg-ink-50 transition-colors select-none"
                onClick={onToggle}
            >
                {/* Avatar */}
                <div className="h-12 w-12 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center text-sm font-extrabold uppercase shrink-0">
                    {initials}
                </div>

                {/* Core info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-ink-900">{u.name}</span>
                        {u.salesDesignation && (
                            <span className="ag-chip bg-brand-teal-50 text-brand-teal-700 text-[10px]">
                                {DESIG_LABEL[u.salesDesignation] || u.salesDesignation.toUpperCase()}
                            </span>
                        )}
                        <span className={`ag-chip text-[10px] ${USER_STATUS_CHIP[u.status] || 'bg-ink-100 text-ink-600'}`}>
                            {u.status}
                        </span>
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5">{u.email}</div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {u.phone && (
                            <span className="text-[11px] text-ink-400 inline-flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {u.phone}
                            </span>
                        )}
                        {u.officeBranch && (
                            <span className="text-[11px] text-ink-400 inline-flex items-center gap-1">
                                <Building2 className="h-3 w-3" /> {u.officeBranch}
                            </span>
                        )}
                    </div>
                </div>

                {/* Expand toggle */}
                <div className="text-ink-400 shrink-0">
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
            </div>

            {/* Expanded detail panel */}
            {expanded && (
                <div className="border-t border-ink-100 px-5 py-5 bg-ink-50/50 space-y-5">
                    {/* Details grid */}
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-3">Profile Details</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <Detail icon={Mail}        label="Email"          value={u.email} />
                            <Detail icon={Phone}       label="Phone"          value={u.phone || '—'} />
                            <Detail icon={Briefcase}   label="Designation"    value={u.designation || '—'} />
                            <Detail icon={Star}        label="Sales Role"     value={DESIG_LABEL[u.salesDesignation] || u.salesDesignation || '—'} />
                            <Detail icon={Building2}   label="Office Branch"  value={u.officeBranch || '—'} />
                            <Detail icon={MapPin}      label="Territory"      value={u.territory || '—'} />
                            <Detail icon={UserIcon}    label="System Role"    value={u.role || '—'} />
                            <Detail icon={Calendar}    label="Joined"         value={u.onboardedAt ? fd(u.onboardedAt) : u.createdAt ? fd(u.createdAt) : '—'} />
                            <Detail icon={CheckCircle2} label="Email Verified" value={u.emailVerifiedAt ? fd(u.emailVerifiedAt) : 'Not verified'} />
                        </div>
                    </div>

                    {/* Zoho CRM identity */}
                    <div className="ag-card p-4 bg-white">
                        <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-3">Zoho CRM Identity</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                            <div className="space-y-0.5">
                                <div className="text-ink-400 text-[10px] uppercase tracking-wider">Zoho User ID</div>
                                <div className="font-mono font-semibold">
                                    {u.zohoUserId || <span className="text-ink-400 italic font-sans font-normal">not set</span>}
                                </div>
                            </div>
                            <div className="space-y-0.5">
                                <div className="text-ink-400 text-[10px] uppercase tracking-wider">Match Email</div>
                                <div className="font-semibold">{u.email}</div>
                            </div>
                        </div>
                    </div>

                    {/* Skills */}
                    {u.skills?.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-2">Skills</div>
                            <div className="flex flex-wrap gap-1.5">
                                {u.skills.map(s => (
                                    <span key={s} className="ag-chip bg-sky-50 text-sky-700">{s}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Reports-to */}
                    {u.reportsTo && (
                        <div className="text-xs text-ink-500 flex items-center gap-1.5">
                            <UserIcon className="h-3.5 w-3.5 text-ink-400" />
                            Reports to: <span className="font-semibold text-ink-800">{u.reportsTo.name || u.reportsTo}</span>
                            {u.reportsTo.email && <span className="text-ink-400">({u.reportsTo.email})</span>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2.5">
            <Icon className="h-3.5 w-3.5 text-ink-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">{label}</div>
                <div className="text-xs text-ink-800 font-medium break-all">{value}</div>
            </div>
        </div>
    );
}
